import type { FC, ReactElement } from 'react';
import { useCallback, useMemo } from 'react';

import { useCashierCallbackConnected } from '../../../../hooks/useCashierCallbackConnected';
import { useCoreDispatch } from '../../../../store/CoreStore';
import { useBetSlice } from '../../../../store/slices/bets/selectors';
import { useChatSlice } from '../../../../store/slices/chat/selectors';
import { chatActions } from '../../../../store/slices/chat/slice';
import { useConfigSlice } from '../../../../store/slices/config/selectors';
import { useGameStateSlice } from '../../../../store/slices/game/selectors';
import { useIsSmallerThanWide } from '../../../../styles/hooks/media';
import { isGameChatRestricted } from '../../../../utils/chat';

import { ChatFooter } from './ChatFooter';

export interface ChatFooterConnectedProps {
  isGameChat?: boolean;
}

export const ChatFooterConnected: FC<ChatFooterConnectedProps> = ({ isGameChat = false }): ReactElement => {
  const userId = useConfigSlice.playerConfig()?.userId ?? '';
  const casinoId = useConfigSlice.session()?.casinoId ?? '';
  const tableId = useConfigSlice.session()?.tableId ?? '';
  const gameType = useConfigSlice.session()?.gametype?.toUpperCase() ?? '';
  const dealerName = useGameStateSlice.dealer()?.name ?? '';
  const isGameChatBlocked = useChatSlice.getIsGameChatBlocked();
  const isLiveSupportChatBlocked = useChatSlice.getIsLiveSupportChatBlocked();
  const isBetChatAllowed = useChatSlice.getIsBetChatAllowed();
  const isBetRequiredToChat = useChatSlice.isBetRequiredToChat();
  const isLowBalance = useBetSlice.isLowBalance();
  const hasTotalBet = useBetSlice.hasTotalBet();
  const isPrivateTable = useConfigSlice.isPrivateTable();
  const isSpectator = useConfigSlice.isSpectatorUser();
  const isChatDisabledForJurisdiction = useConfigSlice.getIsChatDisabledForJurisdiction();
  const isMobile = useIsSmallerThanWide();
  const dispatch = useCoreDispatch();

  const cashierCallback = useCashierCallbackConnected();

  const isGameChatUnavailable = useMemo(
    () => isGameChatRestricted(isBetRequiredToChat, isBetChatAllowed, isLowBalance, hasTotalBet),
    [hasTotalBet, isBetChatAllowed, isBetRequiredToChat, isLowBalance]
  );

  const isGameChatDisabled = isPrivateTable ? isChatDisabledForJurisdiction || isSpectator : isGameChatBlocked;

  const onInputFocus = useCallback(
    (inputFocused: boolean) => {
      if (isPrivateTable && isMobile && inputFocused) {
        dispatch(chatActions.setIsPrivateOptionsExpanded(false));
      }
    },
    [isPrivateTable, isMobile, dispatch]
  );

  return (
    <ChatFooter
      cashierCallback={cashierCallback}
      casinoId={casinoId}
      dealerName={dealerName}
      isBetRequiredToChat={isBetRequiredToChat}
      isGameChat={isGameChat}
      isGameChatBlocked={isGameChatDisabled}
      isLiveSupportChatBlocked={isLiveSupportChatBlocked || isSpectator}
      isGameChatUnavailable={isGameChatUnavailable && isGameChat && !isPrivateTable}
      tableId={tableId}
      userId={userId}
      isSpectator={isSpectator}
      gameType={gameType}
      onInputFocus={onInputFocus}
    />
  );
};
